import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success('Password reset successful!');
      
      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Success State
  if (success) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border-2 border-black hard-shadow-lg max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Password Reset!
          </h1>
          <p className="text-gray-600 mb-6">
            Your password has been reset successfully. You can now login with your new password.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#FF6B6B] text-white font-bold py-3 px-8 rounded-full border-2 border-black hard-shadow-hover"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Error State (invalid/missing token)
  if (error && !token) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border-2 border-black hard-shadow-lg max-w-md w-full p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Invalid Link
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#FF6B6B] text-white font-bold py-3 px-8 rounded-full border-2 border-black hard-shadow-hover"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Reset Form
  return (
    <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border-2 border-black hard-shadow-lg max-w-md w-full p-8">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Set New Password
        </h1>
        <p className="text-gray-600 mb-6">
          Enter your new password below.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold mb-2">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
              required
              minLength={6}
              className="w-full border-2 border-black rounded-lg p-3 focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)]"
              placeholder="At least 6 characters"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="confirm-password-input"
              required
              className="w-full border-2 border-black rounded-lg p-3 focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)]"
              placeholder="Re-enter your password"
            />
          </div>

          <button
            type="submit"
            data-testid="reset-password-submit"
            disabled={loading}
            className="w-full bg-[#FF6B6B] text-white font-bold py-3 px-6 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 hover:text-black font-medium"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
